import React, { useMemo, useState } from "react";
import { SHIFT_PREFS, shortPref, prefClass } from "../shiftPrefs";
import "./Sidebar.css";

function formatTime(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}
function formatDate(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  const today = new Date();
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === tomorrow.toDateString()) return "Tomorrow";
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}
function isUrgent(ts) {
  if (!ts) return false;
  const diff = new Date(ts) - Date.now();
  return diff > 0 && diff < 4 * 60 * 60 * 1000;
}

export default function Sidebar({
  mode, setMode, shifts, clients, workers = [], weeklySchedule = {},
  selectedShift, selectedClient, selectedEmployee,
  onSelectShift, onSelectClient, onSelectEmployee, loading,
  roleFilter = "all", setRoleFilter, shiftPrefFilter = "all", setShiftPrefFilter,
  filtersActive, onClearFilters,
}) {
  const [empSearch, setEmpSearch] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [customAddr, setCustomAddr] = useState("");

  const sortedShifts = useMemo(() =>
    [...shifts].sort((a, b) => new Date(a.scheduledStart) - new Date(b.scheduledStart)), [shifts]);

  const sortedEmployees = useMemo(() =>
    [...workers].sort((a, b) =>
      `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)), [workers]);

  const filteredEmployees = useMemo(() => {
    const q = empSearch.trim().toLowerCase();
    return sortedEmployees.filter(w =>
      (!q || `${w.firstName} ${w.lastName}`.toLowerCase().includes(q)) &&
      (roleFilter === "all" || w.role === roleFilter) &&
      (shiftPrefFilter === "all" || (w.shiftPref || []).includes(shiftPrefFilter))
    );
  }, [sortedEmployees, empSearch, roleFilter, shiftPrefFilter]);

  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(c =>
      (c.fullName || "").toLowerCase().includes(q) ||
      (c.address || "").toLowerCase().includes(q));
  }, [clients, clientSearch]);

  // Ad-hoc address: find nearby caregivers for a client not yet in the system.
  const submitCustomAddr = (e) => {
    e.preventDefault();
    const addr = customAddr.trim();
    if (addr.length < 6) return;
    onSelectClient({ id: "__custom__", fullName: "New address", address: addr, custom: true });
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-top">
        <div className="mode-tabs">
          <button className={`mode-tab ${mode === "shifts" ? "active" : ""}`} onClick={() => setMode("shifts")}>
            Open Shifts {shifts.length > 0 && <span className="tab-count">{shifts.length}</span>}
          </button>
          <button className={`mode-tab ${mode === "clients" ? "active" : ""}`} onClick={() => setMode("clients")}>
            Clients
          </button>
          <button className={`mode-tab ${mode === "employees" ? "active" : ""}`} onClick={() => setMode("employees")}>
            Employees
          </button>
        </div>
      </div>

      {mode === "employees" && (
        <div className="sidebar-search">
          <input
            className="emp-search-input"
            type="text"
            placeholder="Search employees…"
            value={empSearch}
            onChange={e => setEmpSearch(e.target.value)}
          />
          <div className="sidebar-filters">
            <select className="filter-select" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
              <option value="all">All roles</option>
              <option value="CNA">CNA</option>
              <option value="LPN">LPN</option>
              <option value="RN">RN</option>
            </select>
            <select className="filter-select" value={shiftPrefFilter} onChange={e => setShiftPrefFilter(e.target.value)}>
              <option value="all">Any shift</option>
              {SHIFT_PREFS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          {filtersActive && (
            <button className="filter-clear sidebar-clear" onClick={onClearFilters} title="Clear both filters">
              ✕ Clear filters
            </button>
          )}
        </div>
      )}

      {mode === "clients" && (
        <div className="sidebar-search">
          <input
            className="emp-search-input"
            type="text"
            placeholder="Search clients…"
            value={clientSearch}
            onChange={e => setClientSearch(e.target.value)}
          />
          <form className="custom-addr" onSubmit={submitCustomAddr}>
            <input
              className="emp-search-input"
              type="text"
              placeholder="…or type any full address (new client)"
              value={customAddr}
              onChange={e => setCustomAddr(e.target.value)}
            />
            <button type="submit" className="custom-addr-btn" disabled={customAddr.trim().length < 6}>
              Find nearby caregivers
            </button>
          </form>
        </div>
      )}

      <div className="sidebar-list">
        {loading && <div className="sidebar-loading"><span className="spinner" /> Loading…</div>}

        {!loading && mode === "shifts" && sortedShifts.map(shift => (
          <button key={shift.id} className={`list-item ${selectedShift?.id === shift.id ? "selected" : ""}`} onClick={() => onSelectShift(shift)}>
            <div className="item-name">{shift.clientName || "Unknown Client"}</div>
            <div className="item-meta">
              <span className="item-date">{formatDate(shift.scheduledStart)}</span>
              <span className="item-time">{formatTime(shift.scheduledStart)} – {formatTime(shift.scheduledEnd)}</span>
            </div>
            <div className="item-badges">
              {shift.role && <span className={`badge role-${shift.role?.toLowerCase()}`}>{shift.role}</span>}
              {isUrgent(shift.scheduledStart) && <span className="badge urgent">URGENT</span>}
            </div>
          </button>
        ))}
        {!loading && mode === "shifts" && sortedShifts.length === 0 && <div className="sidebar-empty">No open shifts</div>}

        {!loading && mode === "clients" && filteredClients.map(client => (
          <button key={client.id} className={`list-item ${selectedClient?.id === client.id ? "selected" : ""}`} onClick={() => onSelectClient(client)}>
            <div className="item-name">{client.fullName}</div>
            {client.address && <div className="item-address">{client.address}</div>}
          </button>
        ))}
        {!loading && mode === "clients" && filteredClients.length === 0 && (
          <div className="sidebar-empty">{clientSearch ? "No matching clients" : "No clients found"}</div>
        )}

        {!loading && mode === "employees" && filteredEmployees.map(w => {
          const hrs = weeklySchedule?.[w.id]?.totalHours || 0;
          const ot = hrs >= 40;
          return (
            <button key={w.id} className={`list-item ${selectedEmployee?.id === w.id ? "selected" : ""}`} onClick={() => onSelectEmployee(w)}>
              <div className="item-name">{w.firstName} {w.lastName}</div>
              <div className="item-badges">
                {w.role && <span className={`badge role-${w.role.toLowerCase()}`}>{w.role}</span>}
                {ot
                  ? <span className="badge ot">⚠ {hrs.toFixed(1)}h</span>
                  : (hrs > 0 && <span className="item-hrs">{hrs.toFixed(1)}h</span>)}
              </div>
              {w.shiftPref?.length > 0 && (
                <div className="pref-chips item-prefs">
                  {w.shiftPref.map(p => <span key={p} className={`pref-chip ${prefClass(p)}`}>{shortPref(p)}</span>)}
                </div>
              )}
            </button>
          );
        })}
        {!loading && mode === "employees" && filteredEmployees.length === 0 && <div className="sidebar-empty">No employees found</div>}
      </div>
    </aside>
  );
}
